import { FC, useState } from 'react';
import { Box, Center, Flex, HStack } from '@chakra-ui/react';
import { differenceInDays } from 'date-fns';

import Button from 'components/_core/Button';
import Heading from 'components/_core/Heading';
import ErrorCard from 'components/ErrorCard';
import { logEvent } from 'features/Analytics/analytics';
import dashboardLocales from 'features/Dashboard/common/locales';
import * as ContentLayout from 'layouts/ContentLayout';

import { DashboardTransaction } from '../../../../__generated__/GQL';
import useCompanyContext from '../../../../common/hooks/useCompanyContext';
import { BetaTag } from '../components/BetaTag';
import DashboardOnboardingModal from '../components/DashboardOnboardingModal';
import IntervalContextProvider from '../components/IntervalContext';
import { LoadingIntervalSkeleton } from '../components/LoadingIntervalSkeleton';
import RangeIntervalPicker from '../components/RangeIntervalPicker';
import {
  getDefaultMonthlyValue,
  RangeIntervalDate,
} from '../components/RangeIntervalPicker/utils';
import useDashboardNeedsEmptyState from '../hooks/useDashboardNeedsEmptyState';
import { useDashboardQuery } from '../hooks/useDashboardQuery';
import useDashboardUnavailableInterval from '../hooks/useDashboardUnavailableInterval';
import GraphSelector, { GraphType } from './components/GraphSelector';
import GraphWithFilter from './components/GraphWithFilter';
import locales from './locales';

const getDateTickInterval = (interval: RangeIntervalDate) => {
  const WEEKLY_INTERVAL = 6;

  if (interval.type === 'quarter') {
    return WEEKLY_INTERVAL;
  }

  // Return weekly interval if the custom period is more than a month but less than a quarter
  if (interval.type === 'custom') {
    const dayDifference = differenceInDays(
      interval.currentInterval.to,
      interval.currentInterval.from,
    );

    if (dayDifference <= 92 && dayDifference >= 31) {
      return WEEKLY_INTERVAL;
    }
  }

  return undefined;
};

const Overview: FC = () => {
  const [interval, setInterval] = useState<RangeIntervalDate>(
    getDefaultMonthlyValue(),
  );
  const { companyProfileId, companyUserId } = useCompanyContext();
  const [currentGraphType, setCurrentGraphType] =
    useState<GraphType>('balance');

  const {
    balance,
    cumulativePayin,
    cumulativePayout,
    error,
    granularity,
    merchants,
    newBalanceTotal,
    newPayinTotal,
    newPayoutTotal,
    oldBalanceTotal,
    oldPayinTotal,
    oldPayoutTotal,
    payin,
    paymentMethod,
    payout,
    teamMembers,
  } = useDashboardQuery({
    endDate: interval.currentInterval.to,
    previousEndDate: interval.previousInterval?.to,
    previousStartDate: interval.previousInterval?.from,
    startDate: interval.currentInterval.from,
  });

  const needsEmptyState = useDashboardNeedsEmptyState({
    balance,
    payin,
    payout,
  });

  const needsUnavailableDateState = useDashboardUnavailableInterval(
    interval.currentInterval.from,
    interval.currentInterval.to,
  );

  const isFutureData = (date: string | null): boolean => {
    if (!date) {
      return true;
    }
    return new Date(date) > new Date();
  };

  const hideFuturePoints = (
    dashboardData: DashboardTransaction[] | undefined,
  ): DashboardTransaction[] | undefined => {
    if (!dashboardData) {
      return undefined;
    }

    return dashboardData.map((data) => ({
      ...data,
      newValue: isFutureData(data.newDate) ? null : data.newValue,
      oldValue: isFutureData(data.oldDate) ? null : data.oldValue,
    }));
  };

  const getGraphData = () => {
    switch (currentGraphType) {
      case 'balance':
        return hideFuturePoints(balance);
      case 'payin':
        return hideFuturePoints(cumulativePayin);
      case 'payout':
        return hideFuturePoints(cumulativePayout);
      default:
        return balance;
    }
  };

  const graphData = getGraphData();
  const dateTickInterval = getDateTickInterval(interval);

  const graphSelectorClick = (graphType: GraphType) => {
    logEvent({
      name: 'Dashboard Section Clicked',
      properties: {
        companyProfileId,
        companyUserId,
        graphType,
        source: 'web-app',
      },
      usePrefix: false,
    });
    setCurrentGraphType(graphType);
  };

  if (error) {
    return (
      <Center height="100%">
        <ErrorCard />
      </Center>
    );
  }

  return (
    <ContentLayout.Root>
      <DashboardOnboardingModal />
      {/* Keep paddingX={100} to avoid the tooltips to be cut by the navbar on window resize */}
      <ContentLayout.Body paddingX={100} size="md">
        <HStack
          alignItems="center"
          justifyContent="space-between"
          marginBottom="space-32"
        >
          <HStack spacing="space-8">
            <Heading size="lg">{locales.title}</Heading>

            <BetaTag showTooltip />
          </HStack>

          <a
            href={dashboardLocales.feedback.feedbackLink}
            rel="noopener noreferrer"
            target="_blank"
          >
            <Button icon="link" variant="secondary">
              {dashboardLocales.feedback.feedbackCTA}
            </Button>
          </a>
        </HStack>

        {balance ? (
          <RangeIntervalPicker onChange={setInterval} value={interval} />
        ) : (
          <LoadingIntervalSkeleton />
        )}

        <IntervalContextProvider granularity={granularity} interval={interval}>
          <Flex gap="space-24" marginTop="space-24" minWidth="900px">
            <Box flex="1" minWidth="284px">
              <GraphSelector
                displayArrow={!needsEmptyState && !needsUnavailableDateState}
                empty={(needsEmptyState || needsUnavailableDateState) ?? false}
                evolutionColorSchema="arrow-neutral"
                isLoading={!balance}
                isSelected={currentGraphType === 'balance'}
                newTotal={newBalanceTotal ?? 0}
                oldTotal={oldBalanceTotal ?? 0}
                onClick={() => graphSelectorClick('balance')}
                title={locales.balanceTitle}
              />
            </Box>
            <Box flex="1" minWidth="284px">
              <GraphSelector
                empty={(needsEmptyState || needsUnavailableDateState) ?? false}
                evolutionColorSchema="reversed"
                isLoading={!cumulativePayout}
                isSelected={currentGraphType === 'payout'}
                newTotal={newPayoutTotal}
                oldTotal={oldPayoutTotal}
                onClick={() => graphSelectorClick('payout')}
                title={locales.payoutTitle}
              />
            </Box>
            <Box flex="1" minWidth="284px">
              <GraphSelector
                empty={(needsEmptyState || needsUnavailableDateState) ?? false}
                isLoading={!cumulativePayin}
                isSelected={currentGraphType === 'payin'}
                newTotal={newPayinTotal ?? 0}
                oldTotal={oldPayinTotal ?? 0}
                onClick={() => graphSelectorClick('payin')}
                title={locales.payinTitle}
              />
            </Box>
          </Flex>

          <Box height="space-16" />

          <GraphWithFilter
            dateTickInterval={dateTickInterval}
            empty={needsEmptyState ?? false}
            filtersData={{
              merchants,
              paymentMethod,
              teamMembers,
            }}
            graphData={graphData}
            graphType={currentGraphType}
            hasUnavailableDateRange={needsUnavailableDateState ?? false}
          />
        </IntervalContextProvider>
      </ContentLayout.Body>
    </ContentLayout.Root>
  );
};

export default Overview;
